import { IMG } from "./homeContent";

// PRIORITY 3 — the deeper Doorway / offer pages (Task List §45–§52).
//
// Unlike Priority 1 and 2, the brief supplies *direction* for these pages, not finished
// copy. Everything below is written to that direction and to the editorial guardrails in
// GM.com OVERALL WEBSITE DIRECTIVE — flowing prose with varied sentence structure, no
// stereotypical AI cadence, no single-phrase paragraphs, no sentence trying to be a
// tagline, offer summaries kept near 80–160 words, no unsupported clinical claims, and
// no public pricing (§7). It is therefore DRAFT COPY FOR CLIENT APPROVAL, not transcription.
//
// Every page follows the §45 conversion order:
//   emotional/problem hero → promise → who this is for → what it actually is →
//   why it's different → relevant credibility → proof → what happens → objections → CTA
//
// Testimonials are drawn from the eight approved quotes and placed where they are
// genuinely relevant, per §17 ("testimonials can also appear later contextually").

export const DOORWAY_PAGES = {
  // ── DOORWAY 01 · §46 (and §52, which lives here) ─────────────────────────
  yourself: {
    id: "human-capacities",
    doorway: "Human Capacities",
    n: "01",
    img: IMG.contemplation,

    hero: {
      h: "Bringing more of who you are to how you live and lead.",
      sub: "Most of us learned to lead with the mind alone. It is brilliant, and it was never meant to play the whole orchestra.",
    },

    promise:
      "This is work for people who are effective already, and who sense that something has gone quiet underneath the effectiveness. Not a course in being calmer, and not a retreat from ambition — a way of bringing your full intelligence back into the room with you: emotional truth alongside analysis, intuition alongside strategy, presence alongside performance.",

    who: {
      h: "Who this is for",
      items: [
        "Leaders carrying more than they let on",
        "Coaches, therapists and practitioners who hold space for others",
        "Founders and executives navigating a demanding season",
        "Anyone who has done the outer work and senses there is inner work waiting",
      ],
    },

    experience: {
      h: "What the experience actually is",
      body: "The Resonance Leadership Immersion is a two-day experience led by Gary Malkin with Adam Gaskill. Gary works with music, deep listening and contemplative practice to change the conditions in the room, so that what usually stays unspoken becomes available. Adam works with strategy, behaviour and execution, so that what opens during the two days has somewhere to go afterwards. The group is kept deliberately small.",
      blocks: [
        { t: "Coming back into your own signal", d: "The presence other people can feel, and which no amount of preparation can substitute for." },
        { t: "Truth and clarity", d: "Time and quiet enough to separate what is actually true from what has simply gone unquestioned." },
        { t: "The stories underneath", d: "Identity and belief run quietly beneath most decisions. This is where they become visible." },
        { t: "Integration that survives contact", d: "You leave with a concrete ninety-day path, not a feeling that fades by Thursday." },
      ],
    },

    different: {
      h: "Why this is different",
      body: "Most leadership work asks you to add something — a framework, a discipline, a morning routine. This asks you to stop leaving parts of yourself outside the room. The pairing matters: inner development on its own tends to stay a lovely memory, and execution on its own tends to run dry. Gary and Adam work the two together deliberately, which is the whole premise of the Immersion.",
    },

    credibility:
      "Gary has spent more than four decades exploring how music, emotion and presence change what becomes possible between people — in hospice rooms, boardrooms, concert halls and small gatherings. Adam Gaskill brings strategy, human behaviour and execution. The Immersion is where those two bodies of work meet.",

    proof: {
      quote: "This new way to experience wisdom can bring your heart, mind and soul into deeper coherence, offering a breakthrough through which you will feel more receptive, connected and alive. Gary Malkin has come up with a new technology for experiencing your Spirit.",
      name: "Deepak Chopra",
      title: "Bestselling Author and Spiritual Teacher",
    },

    process: {
      h: "What happens",
      steps: [
        { n: "01", t: "A conversation", d: "We talk about where you are and what you are actually carrying. No pitch." },
        { n: "02", t: "Application", d: "The group is small, so fit matters in both directions." },
        { n: "03", t: "Two days together", d: "The immersion itself, held in a setting chosen for quiet." },
        { n: "04", t: "Ninety days", d: "A structured path forward, so the work continues after the room empties." },
      ],
    },

    faq: [
      { q: "Is this therapy?", a: "No. It is an educational and experiential immersion, not treatment, and it is not a substitute for therapy or medical care. Many participants have their own therapeutic support and find the two work well alongside each other." },
      { q: "Do I need any background in this kind of work?", a: "None. People arrive from very different starting points — some with a long contemplative practice, some with none at all. The work meets you where you are rather than assuming a shared vocabulary." },
      { q: "What does it cost?", a: "The Immersion is offered by application, and investment is discussed in conversation once we understand what you are looking for and whether it is the right fit. We would rather have that conversation properly than publish a number without context." },
      { q: "How is this different from the Portals of Awe Founding Circle?", a: "They are separate. Portals of Awe is an ongoing community and practice you join. The Immersion is a two-day experience with a defined arc and a ninety-day path afterwards. Some people do both; neither requires the other." },
    ],

    cta: { h: "Start with a conversation.", body: "Tell us where you are and what you are carrying. If it is a fit, we will talk about what comes next.", label: "Apply for the Immersion" },
  },

  // ── DOORWAY 02 · §47 ─────────────────────────────────────────────────────
  event: {
    id: "resonance-catalyst",
    doorway: "Resonance Catalyst",
    n: "02",
    img: IMG.keynote2,

    hero: {
      h: "When the room changes, everything after it lands differently.",
      sub: "You already know the difference between an audience that has heard something and an audience that has felt it.",
    },

    promise:
      "Gary is not a speaker who happens to play piano, and not a musician filling a gap in the agenda. He works on the emotional and relational architecture of a gathering — what people are willing to say, how closely they listen, whether the room becomes a group of individuals or something more coherent. That shift is the point, and everything programmed afterwards benefits from it.",

    who: {
      h: "Who this is for",
      items: [
        "Conference and event producers",
        "Retreat and mastermind leaders",
        "Executive and organisational gatherings",
        "Communities, foundations and family offices",
      ],
    },

    experience: {
      h: "What the experience actually is",
      body: "The shape is designed around your gathering rather than dropped into it. Gary may open or close the room, underscore another speaker's most difficult moment live from the piano, guide a period of collective listening, or hold a participatory experience where the room itself becomes the instrument. Most engagements combine several of these, and the combination is decided with you beforehand.",
      blocks: [
        { t: "Keynote", d: "A talk that lands in the body as well as the mind, built around ideas your audience is already circling." },
        { t: "Live underscoring", d: "Real-time music beneath another speaker, a ceremony, or a moment the agenda cannot script." },
        { t: "Immersive experience", d: "A designed emotional arc across a session or a whole day, from opening to close." },
        { t: "Participatory and ceremonial", d: "Collective listening or singing, where belonging is experienced before it is discussed." },
      ],
    },

    different: {
      h: "Why this is different",
      body: "A speaker occupies a slot. A catalyst changes the conditions the rest of the programme runs in. That difference shows up in what people are willing to say in the session after Gary's, in how the room treats each other at dinner, and in what participants still describe months later. It is also why the work is shaped in conversation rather than booked from a menu.",
    },

    credibility:
      "Gary has performed and spoken in concert halls, hospitals, boardrooms and living rooms, and closed a festival of thirty thousand people in Portugal. He has scored for film and television for decades and worked alongside many of the wisdom keepers whose gatherings he now helps shape.",

    proof: {
      quote: "In a world fragmented by distractions, the transformances created by Sarah McCrum and Gary Malkin provide a potent antidote. These short films are not just visual experiences—they are catalysts for collective coherence, bringing a deeper sense of unity and presence that will inspire and elevate your team's dynamics.",
      name: "Jim Garrison",
      title: "President, Ubiquity University; Host, Humanity Rising",
    },

    process: {
      h: "What happens",
      steps: [
        { n: "01", t: "Tell us about the gathering", d: "Who is in the room, what it is for, and what you want them leaving with." },
        { n: "02", t: "Shape the engagement", d: "We design the combination — keynote, underscoring, immersive, participatory." },
        { n: "03", t: "Prepare with your team", d: "Gary works with your run of show rather than around it." },
        { n: "04", t: "The gathering", d: "And, where it helps, a way for people to stay connected to the experience afterwards." },
      ],
    },

    faq: [
      { q: "Do you travel internationally?", a: "Yes. Travel and logistics are worked out as part of scoping the engagement." },
      { q: "Can Gary work with our existing speakers and run of show?", a: "That is usually where the work is strongest. Underscoring another speaker's most vulnerable moment, or holding the transition into a difficult session, often does more than a standalone slot would." },
      { q: "What size of gathering does this suit?", a: "It has worked at twelve people and at thirty thousand. What matters more than headcount is whether the room is being asked to do something real." },
      { q: "What does it cost?", a: "Engagements are scoped around what you are creating, so we discuss investment once we understand the shape. Tell us about the gathering and we will come back with a proposal." },
    ],

    cta: { h: "Tell us about your gathering.", body: "Share what you are creating and who will be in the room. We will come back with how Gary might shape it.", label: "Enquire About Your Event" },
  },

  // ── DOORWAY 03 · §48 ─────────────────────────────────────────────────────
  legacy: {
    id: "living-legacies",
    doorway: "Living Legacies",
    n: "03",
    img: IMG.candle,

    hero: {
      h: "What if someone could still feel what it was like to know them?",
      sub: "Not a biography, and not a tribute video. Something closer to presence.",
    },

    promise:
      "A Living Legacy is a commissioned work — music, voice, story and media composed around one person's life. It preserves the things a written account tends to lose: the timbre of a voice, the pauses, the humour, the values someone lived rather than stated. Families describe returning to it for years, and finding that it still does what a photograph cannot.",

    who: {
      h: "Who this is for",
      items: [
        "Founders, CEOs and philanthropists thinking about what continues",
        "Families and legacy stewards honouring a parent or elder",
        "Wisdom keepers, artists and teachers with a body of work to pass on",
        "Institutions and foundations preserving a founder's voice and intent",
      ],
    },

    experience: {
      h: "What the experience actually is",
      body: "Every commission begins with listening. Gary and a small team spend time with the person, or with those closest to them, drawing out the stories, values and moments that actually carry the life. From that material comes an original score and a finished work — sometimes an intimate piece for a family, sometimes a film for an institution. The scope is decided together; nothing here is a package.",
      blocks: [
        { t: "A signature portrait", d: "One life rendered in sound and story — the most personal form the work takes." },
        { t: "An expanded edition", d: "A deeper production, drawing on more material and more time together." },
        { t: "A family archive", d: "Multi-generational preservation, gathered while the voices are still here to gather." },
        { t: "A foundation edition", d: "For institutions and enduring missions, where founder intent needs to outlast the founder." },
      ],
    },

    different: {
      h: "Why this is different",
      body: "Most legacy work documents a life from the outside — dates, achievements, a chronology. This works from the inside out, starting with what made the person unmistakably themselves and finding the musical and narrative form that holds it. Because each commission is composed rather than produced to template, the shape, scale and investment are established in conversation rather than chosen from a list.",
    },

    credibility:
      "Graceful Passages, Gary's landmark work weaving spoken wisdom with original music, has reached more than a million people and is used in hospices and homes worldwide. He has spent decades at the thresholds of life — birth, bonding, illness, grief and completion — which is the experience this work draws on.",

    proof: {
      quote: "These inspiring new modalities for delivering wisdom can help connect our deepest selves to what matters most. A multi-sensory feast for the senses!",
      name: "Thomas Moore",
      title: "Bestselling Author of Care of the Soul",
    },

    process: {
      h: "What happens",
      steps: [
        { n: "01", t: "A private conversation", d: "Who the work is for, and what you would want it to hold." },
        { n: "02", t: "Scope and proposal", d: "Together we shape the form, the material needed and the timeline." },
        { n: "03", t: "Listening and gathering", d: "Interviews, recordings, archives — the raw material of a life." },
        { n: "04", t: "Composition and delivery", d: "The work is scored, produced and handed to you in a form built to last." },
      ],
    },

    faq: [
      { q: "Can this be done if the person has already died?", a: "Yes. Many commissions are created from recordings, letters, photographs and the memories of those who knew them well. Where a voice still exists in any form, it can often be woven in." },
      { q: "Is there still time if someone is seriously ill?", a: "Often, yes, and this is work we treat with real urgency when it is needed. Tell us the situation directly and we will be honest with you about what is possible in the time available." },
      { q: "What does a commission cost?", a: "Because each one is composed around a particular life, investment is shaped through conversation — scope first, then a proposal. There is no price list, and we would not want there to be." },
      { q: "Who owns the finished work?", a: "You do. Ownership, permissions and how the work may be used are agreed in writing before the commission begins." },
    ],

    cta: { h: "Begin with a private conversation.", body: "Tell us who this is for. There is no obligation, and no pitch — we will simply talk about what might be possible.", label: "Start a Legacy Conversation" },
  },

  // ── DOORWAY 04 · §49 ─────────────────────────────────────────────────────
  // Framed around humanity in care, not technology. Vital Neuro appears only deeper,
  // never leading. No claims about clinical outcomes, retention or efficacy.
  healthcare: {
    id: "human-centered-healthcare",
    doorway: "Human-Centered Healthcare",
    n: "04",
    img: IMG.handsSun,

    hero: {
      h: "The people who carry others through the hardest hours need carrying too.",
      sub: "Nurses and caregivers stand at the thresholds of birth, illness, recovery and death — often several in a single shift.",
    },

    promise:
      "This work supports the human beings inside the system. It is not a wellness campaign and it does not ask exhausted people to be more resilient. It creates conditions in which caregivers can feel what they have been carrying, reconnect with why they chose this work, and find some steadiness to take back onto the ward — and it treats grief literacy and mortality awareness as professional capacities rather than personal weaknesses.",

    who: {
      h: "Who this is for",
      items: [
        "Hospital, hospice and health-system leaders",
        "Nursing leadership and clinical educators",
        "Palliative, oncology and critical care teams",
        "Chaplaincy, bereavement and family-support services",
      ],
    },

    experience: {
      h: "What the experience actually is",
      body: "The work takes several forms depending on what a team can realistically make time for: sessions built around music and reflection that give people permission to feel what a shift has held, longer renewal experiences for teams carrying sustained loss, and resources staff can return to on their own. Care for the Journey, created for caregivers, remains the foundation this work grew from.",
      blocks: [
        { t: "Team renewal sessions", d: "Held on site, shaped around what your people are actually carrying rather than a generic curriculum." },
        { t: "Grief literacy", d: "Practical language and practice for loss, so it can move rather than accumulate quietly." },
        { t: "Presence in the room", d: "How to remain emotionally available to patients and families without being consumed by it." },
        { t: "Resources between sessions", d: "Music and guided practice staff can use in the moments the schedule does not allow for." },
      ],
    },

    different: {
      h: "Why this is different",
      body: "Most workforce wellbeing programmes address symptoms and leave the emotional weight of the work unnamed. This begins from the weight itself. It also treats caregivers as people doing something meaningful and difficult rather than a retention statistic, which is usually why teams engage with it when they have quietly stopped engaging with everything else.",
    },

    credibility:
      "Care for the Journey was created specifically for healthcare professionals, and Graceful Passages has been used in hospice and palliative settings for over two decades. Gary has spent much of his working life at the bookends of human life — from prenatal bonding and birth through to end of life — which is the continuum this work sits inside.",

    proof: {
      quote: "Gary Malkin's gift, providing musical enhancement to the spoken word during ritual or prayer, is a gift unparalleled. His work provides a healing balm that makes the delivery of wisdom a true blessing.",
      name: "Archbishop Desmond Tutu",
      title: "Renowned Spiritual Leader, Author",
    },

    process: {
      h: "What happens",
      steps: [
        { n: "01", t: "A conversation with your team", d: "What your people are carrying, and what the schedule can realistically hold." },
        { n: "02", t: "Shape the engagement", d: "A single session, a series, or an ongoing programme — designed with you." },
        { n: "03", t: "Deliver on site", d: "Held where your teams already are, at times that work around clinical demands." },
        { n: "04", t: "Continue between", d: "Resources staff can return to on their own, without needing anything scheduled." },
      ],
    },

    faq: [
      { q: "Is this clinical treatment?", a: "No. This is educational and experiential work supporting presence, reflection and renewal. It is not therapy, not a clinical intervention, and not a substitute for mental health care, and we are careful to say so to participants." },
      { q: "Can this fit around shift patterns?", a: "Yes, and it has to. Sessions are designed to work in the time that genuinely exists — including short formats that fit a handover — rather than assuming teams can be released for a day." },
      { q: "Do you make claims about burnout or retention?", a: "No. We are deliberately careful here. Teams report feeling more connected to their work and to each other, and that is what we describe. We do not claim clinical or workforce outcomes we cannot evidence." },
      { q: "Is technology involved?", a: "Not necessarily, and never as the starting point. Where a team is interested, Vital Neuro's music-based neurofeedback can support individual practice, but the work stands entirely without it." },
    ],

    cta: { h: "Start a conversation about your teams.", body: "Tell us what your people are carrying and what your schedule allows. We will suggest a shape that could genuinely work.", label: "Start a Conversation" },
  },

  // ── DOORWAY 05 · §50 ─────────────────────────────────────────────────────
  presentations: {
    id: "presentations-performances",
    doorway: "Presentations and Performances",
    n: "05",
    img: IMG.auditorium,

    hero: {
      h: "Move me. Inspire me. Remind me what matters.",
      sub: "For nearly fifteen years Gary was known for his concerts. He is also a speaker who can hold a room of thirty thousand.",
    },

    promise:
      "Keynotes, conversations and performances that give an audience an experience rather than a summary. Gary brings ideas, story, music and lived experience to the questions people are already carrying — what it means to stay human in an accelerating world, how we lead without leaving ourselves behind, what beauty is for. Depending on the occasion, he comes alone, with musicians, or with a full orchestra.",

    who: {
      h: "Who this is for",
      items: [
        "Keynote bookers and conference producers",
        "Festivals and large-scale events",
        "Organisations marking a milestone or a transition",
        "Retreats, communities and member gatherings",
      ],
    },

    experience: {
      h: "What the experience actually is",
      body: "A presentation might be a keynote with live piano woven through it, a conversation on stage that opens into music, or a performance built entirely around a moment your gathering is marking. Gary has closed a thirty-thousand-person festival in Portugal and played for twelve people in a living room, and the work is shaped for the specific room rather than transported into it.",
      blocks: [
        { t: "Keynotes", d: "Ideas delivered so they are felt as well as understood, with music carrying what argument cannot." },
        { t: "Conversations", d: "On-stage dialogue that stays genuinely open rather than performing spontaneity." },
        { t: "Performances", d: "Custom musical experiences, solo or with an ensemble, built around your occasion." },
        { t: "Orchestral", d: "For large festivals and major events, where scale is part of the meaning." },
      ],
    },

    different: {
      h: "Why this is different",
      body: "Most keynotes end with applause and a slide deck nobody opens. Gary's work is built to leave something behind that people are still carrying at dinner, and often long after. That is partly the music, and partly a refusal to treat an audience as a group who need convincing rather than a group who came hoping to feel something true.",
    },

    credibility:
      "Gary has performed at some of the world's most celebrated concert halls, given the closing performance at a thirty-thousand-person event in Portugal, and spent nearly five decades as a professional composer for film, television and documentary — including the score for THRIVE: What on Earth Will It Take?",

    proof: {
      quote: "Gary Malkin is a public speaker, composer, and performer of music who draws his audiences deep into their core. He has an uncanny ability to know what will bring forth a deeper listening from the audience so that hearts can be opened in just the right way. A rare treasure.",
      name: "Lynne Twist",
      title: "Author, A Committed Life; Co-Founder, Pachamama Alliance",
    },

    process: {
      h: "What happens",
      steps: [
        { n: "01", t: "Tell us about the occasion", d: "The audience, the moment, and what you want them leaving with." },
        { n: "02", t: "Choose the form", d: "Keynote, conversation, performance — or a combination built for the room." },
        { n: "03", t: "Confirm the details", d: "Date, technical requirements, musicians, and how it sits in your programme." },
        { n: "04", t: "The performance", d: "Gary arrives prepared for your gathering specifically, not a version of it." },
      ],
    },

    faq: [
      { q: "Does Gary travel?", a: "Yes, internationally. Travel and technical requirements are agreed as part of confirming the booking." },
      { q: "Can he perform with our own musicians or orchestra?", a: "Yes, and he often does. Where an ensemble or orchestra is already part of your event, that can be built into the piece rather than working around it." },
      { q: "What are the technical requirements?", a: "At minimum a well-maintained piano and good sound. Fuller requirements depend on the form and are shared well ahead of the date." },
      { q: "What does a booking cost?", a: "Fees depend on the form, the scale and the travel involved. Tell us about the occasion and we will come back with a proposal." },
    ],

    cta: { h: "Move your audience.", body: "Tell us about the occasion and the room. We will come back with what Gary could bring to it.", label: "Enquire About Booking" },
  },
};

// Route key → doorway id, so the Starting Point selector and the doorway cards agree
// with the pages they land on.
export const DOORWAY_ROUTE_BY_ID = {
  "human-capacities": "/work/yourself",
  "resonance-catalyst": "/work/event",
  "living-legacies": "/work/legacy",
  "human-centered-healthcare": "/work/healthcare",
  "presentations-performances": "/work/presentations",
  "portals-of-awe": "/portals-of-awe",
};
